import { AssignmentType, CourseSectionSource, QuestionDifficulty, QuestionSource } from '@/models';
import { DiscussionAttemptStatus } from './AssignmentAttempt';

export type AllowedDifficulties =
  | QuestionDifficulty.EASY
  | QuestionDifficulty.MEDIUM
  | QuestionDifficulty.HARD;

export type AllowedSources =
  | QuestionSource.AI
  | QuestionSource.PUBLISHER
  | QuestionSource.INSTRUCTOR;

export enum ActiveStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export enum FlagStatus {
  FLAGGED = 'FLAGGED',
  UNFLAGGED = 'UNFLAGGED',
}

export enum CourseProvisionStatus {
  PROVISIONED = 'PROVISIONED',
  NOT_PROVISIONED = 'NOT_PROVISIONED',
}

export enum AssignedStatus {
  ASSIGNED = 'ASSIGNED',
  UNASSIGNED = 'UNASSIGNED',
}

export enum ThreadFlaggedStatus {
  FLAGGED = 'FLAGGED',
  REJECTED = 'REJECTED',
  APPROVED = 'APPROVED',
}

export type DiscussionAttemptStatusFilters = Record<DiscussionAttemptStatus, boolean>;

export type DifficultyFilters = Record<AllowedDifficulties, boolean>;

export type SourceFilters = Record<AllowedSources, boolean>;

export type ActiveInactiveFilters = Record<ActiveStatus, boolean>;

export type TaskFilters = Record<ActiveStatus | FlagStatus, boolean>;

export type AssignmentFilters = Record<AssignmentType, boolean>;

export type CourseSourceFilters = Record<CourseSectionSource, boolean>;

export type AssignedUnassignedFilters = Record<AssignedStatus, boolean>;

export type ProvisionFilters = Record<CourseProvisionStatus, boolean>;

export interface EnrollmentStatusFilters {
  isActive: boolean;
  isUnenrolled: boolean;
  isOptedOut: boolean;
}

export interface EnrollmentTypeFilters {
  isStudent: boolean;
  isInstructor: boolean;

  isTA: boolean;
}

export interface ChapterPageFilters {
  chapterId?: number;
  pageIdMin?: number;
  pageIdMax?: number;
}

export interface QuestionFilters {
  difficultyFilters: DifficultyFilters;
  sourceFilters: SourceFilters;
  chapterPageFilters: ChapterPageFilters;
}

export type QuestionFiltersWithActiveStatus = QuestionFilters & {
  activeInactiveFilters: ActiveInactiveFilters;
};

export type ThreadStatusFilters = Record<ThreadFlaggedStatus, boolean>;

export const DEFAULT_ACTIVE_FILTERS: ActiveInactiveFilters = {
  [ActiveStatus.ACTIVE]: true,
  [ActiveStatus.INACTIVE]: false,
};

export const DEFAULT_TASK_FILTERS: TaskFilters = {
  [ActiveStatus.ACTIVE]: true,
  [ActiveStatus.INACTIVE]: false,
  [FlagStatus.FLAGGED]: false,
  [FlagStatus.UNFLAGGED]: false,
};

export const DEFAULT_SOURCE_FILTERS: SourceFilters = {
  [QuestionSource.AI]: true,
  [QuestionSource.PUBLISHER]: true,
  [QuestionSource.INSTRUCTOR]: true,
};

export const DEFAULT_DIFFICULTY_FILTERS: DifficultyFilters = {
  [QuestionDifficulty.EASY]: true,
  [QuestionDifficulty.MEDIUM]: true,
  [QuestionDifficulty.HARD]: true,
};

export const DEFAULT_ASSIGNMENT_FILTERS: AssignmentFilters = {
  [AssignmentType.READING_COMPREHENSION]: true,
  [AssignmentType.STUDY_GUIDE]: true,
  [AssignmentType.TUTOR]: true,
  [AssignmentType.QUIZ]: true,
  [AssignmentType.DISCUSSION]: true,
  [AssignmentType.TEST]: true,
};

export const DEFAULT_DISCUSSION_ATTEMPT_STATUS_FILTERS: DiscussionAttemptStatusFilters = {
  [DiscussionAttemptStatus.PARTIAL]: true,
  [DiscussionAttemptStatus.GRADED]: true,
  [DiscussionAttemptStatus.FLAGGED]: true,
};

export type DiscussionAttemptFilters = {
  statusFilters: DiscussionAttemptStatusFilters;
  discussionOptions: Record<number, boolean>;
};

export type ThreadFilters = {
  statusFilters: ThreadStatusFilters;
  discussionOptions: Record<number, boolean>;
};

export const DEFAULT_ENROLLMENT_STATUS_FILTERS: EnrollmentStatusFilters = {
  isActive: true,
  isUnenrolled: false,
  isOptedOut: false,
};

export const DEFAULT_QUESTION_FILTERS: QuestionFilters = {
  sourceFilters: DEFAULT_SOURCE_FILTERS,
  difficultyFilters: DEFAULT_DIFFICULTY_FILTERS,
  chapterPageFilters: {},
};

export const DEFAULT_QUESTION_FILTERS_WITH_ACTIVE_STATUS: QuestionFiltersWithActiveStatus = {
  sourceFilters: DEFAULT_SOURCE_FILTERS,
  difficultyFilters: DEFAULT_DIFFICULTY_FILTERS,
  activeInactiveFilters: DEFAULT_ACTIVE_FILTERS,
  chapterPageFilters: {},
};

export const DEFAULT_ASSIGNED_FILTERS: AssignedUnassignedFilters = {
  [AssignedStatus.ASSIGNED]: true,
  [AssignedStatus.UNASSIGNED]: true,
};

export const DEFAULT_COURSE_SOURCE_FILTERS: CourseSourceFilters = {
  [CourseSectionSource.API]: true,
  [CourseSectionSource.MANUAL]: true,
  [CourseSectionSource.LTI]: true,
};

export const DEFAULT_COURSE_PROVISION_FILTERS: ProvisionFilters = {
  [CourseProvisionStatus.PROVISIONED]: true,
  [CourseProvisionStatus.NOT_PROVISIONED]: false,
};

export const DEFAULT_DISCUSSION_ATTEMPT_FILTERS: DiscussionAttemptFilters = {
  statusFilters: DEFAULT_DISCUSSION_ATTEMPT_STATUS_FILTERS,
  discussionOptions: {},
};

export const DEFAULT_THREAD_STATUS_FILTERS: ThreadStatusFilters = {
  [ThreadFlaggedStatus.FLAGGED]: true,
  [ThreadFlaggedStatus.REJECTED]: true,
  [ThreadFlaggedStatus.APPROVED]: true,
};

export const DEFAULT_THREAD_FILTERS: ThreadFilters = {
  statusFilters: DEFAULT_THREAD_STATUS_FILTERS,
  discussionOptions: {},
};

export type FilterValue = boolean | number | string | undefined;
